import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminGallery from "./AdminGallery";

const Upload = ({ token, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const formUpload = useRef(null);
  const navigate = useNavigate();
  const [showMessage, setShowMessage] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = () => {
    // Update the refreshKey to force re-rendering of the UploadedGallery component
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const showMessageWithType = (type, msg) => {
    setMessageType(type);
    setMessage(msg);
    setShowMessage(true);
  };

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 3000); // Display message for 3 seconds
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  //----------------
  useEffect(() => {
    // Check if the token is present and valid
    const checkToken = () => {
      if (!token) {
        showMessageWithType(
          "error",
          "You need to be logged in to access this page."
        );
        navigate("/adminlogin"); // Redirect to login if no token
        return;
      }

      // check token validity (e.g., decode and check expiration)
      try {
        const { exp } = JSON.parse(atob(token.split(".")[1])); // Decode JWT payload
        if (exp * 1000 < Date.now()) {
          // Token is expired
          showMessageWithType(
            "error",
            "Your session has expired. Please log in again."
          );
          navigate("/adminlogin");
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          "An error occurred while validating your session. Please log in again.";
        showMessageWithType("error", errorMessage);
        navigate("/adminlogin");
      }
    };

    checkToken();
  }, [token, navigate]);

  //----------------

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    // setFile(e.target.files[0]);

    if (selectedFile) {
      const maxFileSize =
        parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE, 10) || 1 * 1024 * 1024; // Default to 1 MB if not set
      // Fetch the allowed image types from the .env file and split into an array
      const allowedTypes = process.env.REACT_APP_ALLOWED_IMAGE_TYPES
        ? process.env.REACT_APP_ALLOWED_IMAGE_TYPES.split(",").map((type) =>
            type.trim()
          )
        : [];
      if (!allowedTypes.includes(selectedFile.type)) {
        showMessageWithType(
          "error",
          "Invalid file type. Please select an image file (JPEG, JPG, PNG, or WEBP)."
        );
        setFile(null);
        formUpload.current.reset();
        return;
      }
      // Validate file size
      if (selectedFile.size > maxFileSize) {
        // const fileSizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
        showMessageWithType(
          "error",
          `File size exceeds the limit of ${process.env.REACT_APP_MAX_IMAGE_SIZE_ERROR}. Please select a smaller file.`
        );
        setFile(null);
        formUpload.current.reset();
        return;
      }
      setFile(selectedFile);
      setMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true); // Set submitting state

    if (localStorage.getItem("token") === null) {
      setIsSubmitting(false);
      navigate("/");
      alert("please login again");
      return;
    }
    if (!file) {
      showMessageWithType("error", "No file selected");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/uploadPhoto`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, //ensure backticks ` are used with bearer $token
            "Content-Type": "multipart/form-data",
          },
        }
      );
      formUpload.current.reset();
      setIsSubmitting(false);
      setFile(null);
      showMessageWithType(response.data.status, response.data.message);

      // Call the onUploadSuccess prop to refresh the gallery
      if (response.status === 200) {
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
        handleRefresh();
        showMessageWithType(response.data.status, response.data.message);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Something went wrong, try again later.";
      showMessageWithType("error", errorMessage);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  return (
    <div className="container">
      <div className="admin-wrap">
        <div className="box-head">
          <span>Upload Photo to Gallery</span>
        </div>
        <form
          style={{ width: "100%" }}
          onSubmit={handleSubmit}
          ref={formUpload}
          encType="multipart/form-data"
        >
          <div style={{ justifyContent: "center", textAlign: "center" }}>
            {showMessage && (
              <p className={`${messageType} ${!showMessage && "hidden"}`}>
                {message}
              </p>
            )}

            <div className="uploadfile-control">
              <input
                type="file"
                onChange={handleFileChange}
                accept={process.env.REACT_APP_ALLOWED_FILE_TYPES}
                name="image"
              />
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="admin-wrap" style={{ width: "auto" }}>
        <AdminGallery key={refreshKey} token={token} />{" "}
      </div>
    </div>
  );
};
export default Upload;
